import {
  Brain, Terminal, Scale, Compass,
  BookOpen, PenLine, Languages, RefreshCcw,
  Columns3, Quote, SearchCheck, Palette,
} from "lucide-react";

const courses = [
  {
    label: "Summer 2026",
    title: "Thinking in the Age of\nArtificial Intelligence",
    subtitle:
      "A two-week intensive for high school students who want to understand AI deeply — and use it without losing themselves to it.",
    quote:
      '"If AI can do the hard thinking for us — what is left of a human life?"',
    quoteAttribution: "The question this course is built around",
    details: [
      "Late June / July 2025",
      "2 hours/day \u00b7 5 days/week \u00b7 2 weeks",
      "Online & In-Person cohorts",
      "Grades 9\u201312",
    ],
    pillars: [
      {
        icon: Brain,
        heading: "AI Literacy",
        text: "What is an LLM? What are tokens, models, and training data? What does AI actually \"know\" — and what are its real limitations?",
      },
      {
        icon: Terminal,
        heading: "Prompt Engineering",
        text: "Learn to manipulate variables, compare outputs across models, and evaluate which AI tool is right for a given task.",
      },
      {
        icon: Scale,
        heading: "Philosophical Reasoning",
        text: "Applied epistemology and logic: what counts as knowledge, how do we evaluate claims, and where does human judgment still matter?",
      },
      {
        icon: Compass,
        heading: "Self-Directed Thinking",
        text: "How to stay an independent, original thinker when powerful tools can think on your behalf.",
      },
    ],
    accent: "#2d6a4f",
    accentLight: "rgba(45,106,79,0.12)",
  },
  {
    label: "Year-Round",
    title: "Reading & Writing\nCoaching",
    subtitle:
      "One-on-one and small-group coaching that builds the close-reading habits and clear-writing instincts students need across every subject.",
    quote:
      '"The best readers don\'t just decode words — they interrogate them."',
    quoteAttribution: "The philosophy behind every session",
    details: [
      "Flexible scheduling",
      "1-on-1 or small group",
      "Online & In-Person",
      "Grades 6\u201312",
    ],
    pillars: [
      {
        icon: BookOpen,
        heading: "Close Reading",
        text: "Learn to slow down, annotate, and question a text — moving from surface comprehension to genuine understanding.",
      },
      {
        icon: PenLine,
        heading: "Analytical Writing",
        text: "Structure arguments, integrate evidence, and develop a clear authorial voice across essays, reports, and responses.",
      },
      {
        icon: Languages,
        heading: "Vocabulary in Context",
        text: "Build word knowledge organically through reading — not memorising lists, but encountering and using language in real texts.",
      },
      {
        icon: RefreshCcw,
        heading: "Feedback Cycles",
        text: "Draft, review, revise. Every piece of writing goes through structured feedback so students learn to self-edit.",
      },
    ],
    accent: "#b5833a",
    accentLight: "rgba(181,131,58,0.12)",
  },
  {
    label: "Summer & Term-Time",
    title: "Grammar\nfrom the Ground Up",
    subtitle:
      "A standalone course that teaches grammar not as a set of rules to memorise, but as a system to understand — so students can write with precision and confidence.",
    quote:
      '"Grammar isn\'t about correctness. It\'s about control."',
    quoteAttribution: "What students discover in week one",
    details: [
      "4-week intensive or 12-week term",
      "3 sessions per week",
      "Online & In-Person",
      "Grades 7\u201312",
    ],
    pillars: [
      {
        icon: Columns3,
        heading: "Sentence Architecture",
        text: "Understand how sentences are built — subjects, predicates, clauses, phrases — so you can construct and vary them deliberately.",
      },
      {
        icon: Quote,
        heading: "Punctuation as Meaning",
        text: "Commas, semicolons, dashes, and colons aren't arbitrary — learn what each one does to the rhythm and clarity of a sentence.",
      },
      {
        icon: SearchCheck,
        heading: "Error Patterns",
        text: "Identify your own recurring mistakes and build targeted habits to eliminate them — not through drills, but through revision.",
      },
      {
        icon: Palette,
        heading: "Style & Register",
        text: "Move fluently between formal and informal, academic and conversational. Grammar is the toolkit; style is what you build.",
      },
    ],
    accent: "#6b4c9a",
    accentLight: "rgba(107,76,154,0.12)",
  },
];

export default function CourseShowcase() {
  return (
    <div className="w-full">
      {courses.map((course, i) => (
        <section
          key={i}
          className="w-full border-t border-[#1a1a1a]"
          style={{ background: i % 2 === 0 ? "#0A0A0A" : "#0e0e0e" }}
        >
          <div className="max-w-[900px] mx-auto px-6 sm:px-10 py-24 sm:py-32">
            {/* Label */}
            <span
              className="inline-block text-[11px] font-semibold tracking-[0.12em] uppercase px-3 py-1.5 rounded-full border mb-8"
              style={{
                color: course.accent,
                borderColor: `${course.accent}40`,
              }}
            >
              {course.label}
            </span>

            {/* Title */}
            <h2
              className="whitespace-pre-line m-0 mb-4"
              style={{
                fontSize: "clamp(28px, 4.5vw, 44px)",
                fontWeight: 400,
                lineHeight: 1.15,
                letterSpacing: "-0.01em",
                fontFamily:
                  "'Georgia', 'Times New Roman', 'Garamond', serif",
                color: "#FAFAFA",
              }}
            >
              {course.title}
            </h2>

            {/* Subtitle */}
            <p
              className="max-w-[560px] mb-10"
              style={{
                fontSize: "clamp(15px, 2vw, 17px)",
                lineHeight: 1.7,
                color: "#777",
                fontFamily:
                  "'Georgia', 'Times New Roman', 'Garamond', serif",
              }}
            >
              {course.subtitle}
            </p>

            {/* Quote block */}
            <div
              className="rounded-lg mb-12 px-6 py-5"
              style={{
                borderLeft: `2px solid ${course.accent}`,
                background: course.accentLight,
              }}
            >
              <p
                className="m-0 mb-2"
                style={{
                  fontSize: "clamp(16px, 2.2vw, 20px)",
                  lineHeight: 1.55,
                  color: "#ccc",
                  fontStyle: "italic",
                  fontFamily:
                    "'Georgia', 'Times New Roman', 'Garamond', serif",
                }}
              >
                {course.quote}
              </p>
              <span
                className="text-[11px] font-semibold tracking-[0.08em] uppercase"
                style={{ color: course.accent }}
              >
                {course.quoteAttribution}
              </span>
            </div>

            {/* Details row */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-12">
              {course.details.map((detail, j) => (
                <span
                  key={j}
                  className="flex items-center gap-2 text-[13px] font-medium"
                  style={{ color: "#888" }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: course.accent }}
                  />
                  {detail}
                </span>
              ))}
            </div>

            {/* Pillars grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {course.pillars.map((pillar, k) => (
                <div
                  key={k}
                  className="rounded-xl border border-[#1e1e1e] bg-[#111] px-6 py-5"
                >
                  <pillar.icon
                    size={18}
                    strokeWidth={1.5}
                    className="mb-3"
                    style={{ color: "#fff" }}
                  />
                  <h3
                    className="text-[14px] font-semibold mb-2 tracking-wide"
                    style={{ color: "#ddd" }}
                  >
                    {pillar.heading}
                  </h3>
                  <p
                    className="m-0 text-[13.5px] leading-relaxed"
                    style={{ color: "#777" }}
                  >
                    {pillar.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
